import { Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useMatch, useNavigate } from "react-router";
import type { ClientSummary } from "@patent-studio/api-types";
import { usePastConsentGate } from "@/components/consent/ConsentGate";
import { CommandPalette } from "@/components/ui/CommandPalette";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useClients } from "@/hooks/use-clients";
import { useTask, useTaskList } from "@/hooks/use-tasks";
import { AddClientDialog } from "../clients/AddClientDialog";
import { NewDraftDialog } from "../clients/NewDraftDialog";
import { UNASSIGNED_PATH } from "../clients/paths";
import { KeyboardShortcutsDialog } from "./KeyboardShortcutsDialog";
import {
  type PaletteActs,
  clientStepCommands,
  mainCommands,
} from "./palette-commands";
import { type PaletteShortcut, paletteShortcutFor } from "./palette-shortcut";

/** Every draft the palette can jump to without a search of the server; see the plan's note on the limit. */
const DRAFTS_READ = 100;

type Opened =
  | { dialog: "new-draft"; clientId: string }
  | { dialog: "add-client"; thenDraft: boolean }
  | { dialog: "shortcuts" };

/**
 * The palette and its trigger, at the right of the header.
 *
 * Drawn only past the legal gate, as the feedback button is: the public policy
 * pages wear this header, and nobody there has anything to jump to.
 */
export function HeaderCommandPalette() {
  if (!usePastConsentGate()) return null;
  return <ConnectedCommandPalette />;
}

/** The client whose page is open, or whose draft is, read only while the palette is. */
function useClientInView(
  clients: readonly ClientSummary[],
  open: boolean,
): ClientSummary | undefined {
  const onHub = useMatch("/clients/:clientId");
  const inDraft = useMatch("/drafts/:taskId/*");
  const hubId =
    onHub !== null && onHub.pathname !== UNASSIGNED_PATH
      ? onHub.params.clientId
      : undefined;
  const task = useTask(open ? (inDraft?.params.taskId ?? null) : null);
  const id = hubId ?? task.data?.client_id ?? undefined;
  return clients.find((client) => client.client_id === id);
}

/**
 * Whether a modal is already open, the page's own or one the palette handed
 * over to. The shortcut is a window listener, so nothing else stops it opening
 * the palette over a confirmation half answered.
 */
function aDialogIsOpen(): boolean {
  return (
    document.querySelector(
      '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]',
    ) !== null
  );
}

/** Calls `onPress` on the shortcut, reading the newest `onPress` rather than the one first given. */
function useShortcut(shortcut: PaletteShortcut, onPress: () => void) {
  const onPressRef = useRef(onPress);
  useEffect(() => {
    onPressRef.current = onPress;
  });
  useEffect(() => {
    function pressed(event: KeyboardEvent) {
      if (!shortcut.matches(event)) return;
      event.preventDefault();
      onPressRef.current();
    }
    window.addEventListener("keydown", pressed);
    return () => {
      window.removeEventListener("keydown", pressed);
    };
  }, [shortcut]);
}

/**
 * Hands the focus back to what held it before the palette opened, once a
 * dialog the palette handed over to has closed. Neither dialog has a trigger
 * for Radix to return to, and what each saw focused as it opened was the
 * palette's box, which is gone by then.
 */
function useFocusReturnAfter(opened: Opened | null) {
  const originRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (opened !== null) return undefined;
    const origin = originRef.current;
    // After Radix's own return, which runs on a timer of its own and finds no trigger.
    const timer = setTimeout(() => {
      if (origin?.isConnected && document.activeElement === document.body) {
        origin.focus();
      }
    });
    return () => {
      clearTimeout(timer);
    };
  }, [opened]);
  return () => {
    originRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
  };
}

/**
 * Whether the palette is open and on which question, and the dialog it handed
 * over to, with what each option does to them.
 */
function usePaletteFlow() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [choosingClient, setChoosingClient] = useState(false);
  const [opened, setOpened] = useState<Opened | null>(null);
  const rememberOrigin = useFocusReturnAfter(opened);

  function close() {
    setOpen(false);
    setChoosingClient(false);
  }
  function openPalette() {
    rememberOrigin();
    setOpen(true);
  }
  function handOver(next: Opened) {
    close();
    setOpened(next);
  }
  const acts: PaletteActs = {
    startDraftFor: (clientId) => {
      handOver({ dialog: "new-draft", clientId });
    },
    chooseClientForDraft: () => {
      setChoosingClient(true);
    },
    addClient: () => {
      handOver({ dialog: "add-client", thenDraft: false });
    },
    addClientThenDraft: () => {
      handOver({ dialog: "add-client", thenDraft: true });
    },
    goTo: (path) => {
      close();
      void navigate(path);
    },
    showShortcuts: () => {
      handOver({ dialog: "shortcuts" });
    },
    signOut: () => {
      close();
      logout();
    },
  };
  return {
    open,
    choosingClient,
    opened,
    acts,
    close,
    openPalette,
    pressShortcut: () => {
      if (open) close();
      else if (!aDialogIsOpen()) openPalette();
    },
    backToMain: () => {
      setChoosingClient(false);
    },
    setOpened,
  };
}

function ConnectedCommandPalette() {
  const [shortcut] = useState(() => paletteShortcutFor());
  const flow = usePaletteFlow();
  const { open, choosingClient, acts } = flow;
  const clients = useClients({ enabled: open }).data?.clients ?? [];
  const drafts =
    useTaskList(
      { sort: "-updated_at", limit: DRAFTS_READ },
      // Read afresh on every opening: a draft made in another tab, or by a
      // colleague, is what someone reaching for the palette looks for first.
      { enabled: open, staleTime: 0 },
    ).data?.items ?? [];
  const here = useClientInView(clients, open);
  useShortcut(shortcut, flow.pressShortcut);

  return (
    <>
      <PaletteTrigger shortcut={shortcut} onOpen={flow.openPalette} />
      <CommandPalette
        open={open}
        onOpenChange={(next) => {
          if (!next) flow.close();
        }}
        groups={
          choosingClient
            ? clientStepCommands(clients, acts)
            : mainCommands({ here, clients, drafts }, acts)
        }
        step={
          choosingClient
            ? { name: "New draft", onBack: flow.backToMain }
            : undefined
        }
      />
      <HandedOver
        opened={flow.opened}
        setOpened={flow.setOpened}
        shortcut={shortcut}
      />
    </>
  );
}

/** The dialog the palette handed over to: a new draft, a client to add first, or the shortcuts. */
function HandedOver({
  opened,
  setOpened,
  shortcut,
}: {
  opened: Opened | null;
  setOpened: (next: Opened | null) => void;
  shortcut: PaletteShortcut;
}) {
  const done = () => {
    setOpened(null);
  };
  return (
    <>
      {opened?.dialog === "new-draft" && (
        <NewDraftDialog clientId={opened.clientId} open onClose={done} />
      )}
      {opened?.dialog === "shortcuts" && (
        <KeyboardShortcutsDialog shortcut={shortcut} onClose={done} />
      )}
      <AddClientDialog
        open={opened?.dialog === "add-client"}
        onClose={done}
        onAdded={
          opened?.dialog === "add-client" && opened.thenDraft
            ? (client) => {
                setOpened({ dialog: "new-draft", clientId: client.client_id });
              }
            : undefined
        }
      />
    </>
  );
}

function PaletteTrigger({
  shortcut,
  onOpen,
}: {
  shortcut: PaletteShortcut;
  onOpen: () => void;
}) {
  return (
    <Button
      variant="quiet"
      size="pill"
      aria-label="Search clients, drafts and commands"
      aria-keyshortcuts={shortcut.aria}
      onClick={onOpen}
      className="border-white/15 bg-white/10 text-white/75 hover:bg-white/15 hover:text-white"
    >
      <Search aria-hidden />
      <span className="max-sm:hidden">Search</span>
      <span aria-hidden className="flex gap-0.5 max-sm:hidden">
        {shortcut.keys.map((key) => (
          <kbd key={key} className="font-mono text-[10.5px] text-white/55">
            {key}
          </kbd>
        ))}
      </span>
    </Button>
  );
}
